// TextSpriteMarker.js
import { Coordinate, Util } from "maptalks"
import * as THREE from "three"
import { BaseObject, ThreeLayer } from "maptalks.three" // in some builds it's maptalks.three.BaseObject
import { BaseObjectOptionType } from "maptalks.three/dist/type"
import { isNil, set } from "lodash"

const OPTIONS: TextMarkerOptions = {
  // Texture options
  text: "",
  textAlign: "center",
  color: "#ffffff",
  fontFamily: "sans-serif",
  fontSize: 28,
  fontWeight: 400,
  background: "rgba(0, 0, 0, 0.2)",
  lineHeight: 32,
  padding: 8,
  strokeColor: "#000000",
  strokeWidth: 6,
  strokeStyle: "round",
  // Sprite options
  /* Overall scale multiplier  */
  scale: 1,
  altitude: 0,
  /** Hide marker when zoomed out below this level. */
  minZoom: 0,
  /** Hide marker when zoomed in above this level. */
  maxZoom: 22,
}

export type TextMarkerOptions = BaseObjectOptionType & {
  // Texture options
  text?: string
  textAlign?: CanvasTextAlign
  color?: CanvasFillStrokeStyles["fillStyle"]
  fontFamily?: string
  fontSize?: number
  fontWeight?: number
  background?: CanvasFillStrokeStyles["fillStyle"]
  lineHeight?: number
  strokeColor?: CanvasFillStrokeStyles["strokeStyle"]
  strokeWidth?: number
  strokeStyle?: CanvasLineJoin
  padding?: number
  maxWidth?: number
  // Sprite options
  scale?: number
  altitude?: number
  minZoom?: number
  maxZoom?: number
}

export class TextSpriteMarker extends BaseObject {
  #altitudeOffset: number = 0
  constructor(
    coordinate: Coordinate,
    options: TextMarkerOptions,
    layer: ThreeLayer,
    properties: Record<string, any> = {}
  ) {
    options = Util.extend({}, OPTIONS, options, { layer })
    super()
    this._coordinate = new Coordinate(coordinate)
    this._initOptions(options)
    this._createGroup()

    this.properties = { ...properties }

    const sprite = this._createSprite()

    this.getObject3d().add(sprite)

    this._updatePosition()

    this.type = "TextSpriteMarker"
  }

  getOptions(): TextMarkerOptions {
    return super.getOptions()
  }

  _createSprite() {
    const options = this.getOptions()
    const texture = this._createTextTexture(options.text, options)

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.1,
    })

    const sprite = new THREE.Sprite(material)

    // position will be set in _animation or directly after added to layer
    // scale to match texture aspect
    const w = texture.image.width
    const h = texture.image.height
    const base = 1 / 16 // to bring canvas px to map size-ish

    // Normalize text scale by the current map resolution so sprite size stays visually consistent across zoom levels.
    const normalizedScale = options.scale / this.getMap().getGLRes()

    sprite.scale.set(w * base * normalizedScale, h * base * normalizedScale, 1)

    // Compute world-space height for text and use it as altitude offset.
    this.#altitudeOffset = Math.max(
      h * base * options.scale * 0.5,
      0.05 // minimum lift in world units
    )

    return sprite
  }

  _createTextTexture(text: string, options: TextMarkerOptions = {}) {
    const {
      padding,
      fontSize,
      fontFamily,
      fontWeight,
      lineHeight,
      background,
      color,
      textAlign,
      strokeColor,
      strokeWidth,
      maxWidth,
    } = options || {}

    // 1. create a temp canvas to measure
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")!
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

    // 2. split into paragraphs (explicit \n from user)
    const paragraphs = String(text).split("\n")

    // 3. wrap each paragraph to lines
    const wrappedLines: string[] = []

    paragraphs.forEach((paragraph) => {
      if (isNil(maxWidth) || isNaN(maxWidth)) {
        // no wrapping, keep as-is
        wrappedLines.push(paragraph)
        return
      }

      const words = paragraph.split(/\s+/)
      let currentLine = ""

      words.forEach((word) => {
        const testLine = currentLine ? currentLine + " " + word : word
        const testWidth = ctx.measureText(testLine).width

        if (testWidth > maxWidth && currentLine) {
          // push current and start new
          wrappedLines.push(currentLine)
          currentLine = word
        } else {
          currentLine = testLine
        }
      })

      if (currentLine) {
        wrappedLines.push(currentLine)
      }
    })

    // 4. figure out final canvas size
    const lines = wrappedLines.length ? wrappedLines : [""]
    const widest = Math.max(...lines.map((l) => ctx.measureText(l).width), 0)
    const finalWidth =
      (maxWidth ? Math.min(widest, maxWidth) : widest) + padding * 2
    const finalHeight = lineHeight * lines.length + padding * 2

    canvas.width = finalWidth
    canvas.height = finalHeight

    // 5. reapply styles after resize
    const ctx2 = canvas.getContext("2d")!
    ctx2.font = `${fontWeight} ${fontSize}px ${fontFamily}`
    ctx2.textAlign = textAlign

    // background
    if (background && background !== "transparent") {
      ctx2.fillStyle = background
      ctx2.fillRect(0, 0, canvas.width, canvas.height)
    }

    // 6. draw all lines (stroke first, then fill)
    lines.forEach((line, i) => {
      const y = padding + lineHeight * (i + 0.8)
      let x = padding
      if (textAlign === "center") x = canvas.width / 2
      if (textAlign === "right" || textAlign === "end")
        x = canvas.width - padding

      if (strokeWidth > 0) {
        ctx2.lineWidth = strokeWidth
        ctx2.lineJoin = "round"
        ctx2.miterLimit = 2
        ctx2.strokeStyle = strokeColor
        ctx2.strokeText(line, x, y)
      }

      ctx2.fillStyle = color
      ctx2.fillText(line, x, y)
    })

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true
    texture.minFilter = THREE.LinearFilter
    return texture
  }

  _updatePosition() {
    const options = this.getOptions()
    const layer = options.layer

    if (!layer) return

    const altitude = (options.altitude || 0) + this.#altitudeOffset
    const z = layer.altitudeToVector3(altitude, altitude).x
    const position = layer.coordinateToVector3(this._coordinate, z)
    set(this.properties, "default.position", position)
    this.getObject3d().position.copy(position)
  }

  _animation() {
    const layer = this.getLayer() as ThreeLayer

    if (!this.isAdd || !layer) return

    const zoom = layer.map.getZoom()

    const object3d = this.getObject3d()
    const { minZoom = 0, maxZoom = 22 } = this.getOptions()

    // Hide object outside zoom range
    const visible = zoom >= minZoom && zoom <= maxZoom
    object3d.visible = visible
  }

  setText(text: string) {
    const options = this.getOptions()
    options.text = text

    const newSprite = this._createSprite()
    const group = this.getObject3d()

    // Remove all old sprites from the group (but keep group itself)
    group.children.forEach((child) => group.remove(child))
    group.add(newSprite)

    this._updatePosition()
  }

  setAltitude(altitude: number) {
    return super.setAltitude(altitude + this.#altitudeOffset)
  }
}
